import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, MoreVertical, Pencil, Trash2, Contact, Mail, Phone, Building2, CalendarClock } from "lucide-react";

const STAGES = ["Lead", "Prospect", "Customer", "Past Customer"];
const STAGE_STYLES = {
  Lead: "bg-sky-100 text-sky-700",
  Prospect: "bg-amber-100 text-amber-700",
  Customer: "bg-emerald-100 text-emerald-700",
  "Past Customer": "bg-muted text-muted-foreground",
};

const EMPTY = { name: "", org_name: "", email: "", phone: "", stage: "Lead", last_interaction_date: "", next_follow_up_date: "", notes: "" };

const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtDate = (s) => {
  if (!s) return null;
  const d = new Date(s + "T00:00:00");
  if (isNaN(d)) return s;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export default function CRM() {
  const [records, setRecords] = useState([]);
  const [filter, setFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    try { const r = await api.get("/crm"); setRecords(r.data || []); return r.data || []; }
    catch (e) { toast.error("Could not load your CRM records."); return []; }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditingId(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (rec) => {
    setEditingId(rec.id);
    setForm({ name: rec.name || "", org_name: rec.org_name || "", email: rec.email || "", phone: rec.phone || "",
      stage: rec.stage || "Lead", last_interaction_date: rec.last_interaction_date || "",
      next_follow_up_date: rec.next_follow_up_date || "", notes: rec.notes || "" });
    setDialogOpen(true);
  };

  // Deep-link: open a specific record from a Needs Attention alert (/crm?open=<id>)
  useEffect(() => {
    const openId = params.get("open");
    if (!openId || !records.length) return;
    const rec = records.find((r) => r.id === openId);
    if (rec) openEdit(rec);
    params.delete("open");
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    setSaving(true);
    try {
      if (editingId) { await api.put(`/crm/${editingId}`, form); toast.success("Record updated."); }
      else { await api.post("/crm", form); toast.success("Record added."); }
      setDialogOpen(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the record."); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/crm/${deleting.id}`); toast.success("Record deleted."); setDeleting(null); load(); }
    catch (e) { toast.error("Could not delete the record."); }
  };

  const visible = useMemo(
    () => (filter === "all" ? records : records.filter((r) => r.stage === filter)),
    [records, filter]
  );

  const counts = useMemo(() => {
    const c = { all: records.length };
    STAGES.forEach((s) => { c[s] = records.filter((r) => r.stage === s).length; });
    return c;
  }, [records]);

  const followUpTone = (rec) => {
    if (!rec.next_follow_up_date || rec.stage === "Past Customer") return "text-muted-foreground";
    return rec.next_follow_up_date <= todayStr() ? "text-destructive font-medium" : "text-foreground";
  };

  return (
    <div>
      <PageHeader
        testid="crm-header"
        title="CRM"
        subtitle="Leads, Customers & Follow Ups"
        action={
          <Button onClick={openAdd} data-testid="add-crm-button" className="rounded-full bg-primary hover:bg-primary/90 gap-2">
            <Plus className="h-4 w-4" /> Add record
          </Button>
        }
      />

      <div className="mb-6 w-full sm:w-64">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="rounded-full" data-testid="crm-stage-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages ({counts.all})</SelectItem>
            {STAGES.map((s) => <SelectItem key={s} value={s}>{s} ({counts[s] || 0})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {visible.length === 0 ? (
        <Card className="rounded-3xl border-dashed border-2 border-border p-12 text-center" data-testid="crm-empty">
          <Contact className="h-10 w-10 text-muted-foreground/50 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground">{records.length === 0 ? "No records yet. Add your first lead or customer." : "No records in this stage."}</p>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((rec) => (
            <Card key={rec.id} className="rounded-3xl border-border shadow-soft p-5 flex flex-col" data-testid={`crm-record-${rec.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-heading text-lg text-foreground leading-tight truncate">{rec.name}</h3>
                  {rec.org_name && <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5"><Building2 className="h-3.5 w-3.5" /> {rec.org_name}</p>}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 shrink-0" data-testid={`crm-menu-${rec.id}`}><MoreVertical className="h-4 w-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEdit(rec)} data-testid={`crm-edit-${rec.id}`}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setDeleting(rec)} className="text-destructive" data-testid={`crm-delete-${rec.id}`}><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <Badge className={`mt-3 w-fit rounded-full ${STAGE_STYLES[rec.stage] || "bg-muted"}`} data-testid={`crm-stage-${rec.id}`}>{rec.stage}</Badge>

              <div className="mt-4 space-y-1.5 text-sm">
                {rec.email && <p className="flex items-center gap-2 text-muted-foreground truncate"><Mail className="h-3.5 w-3.5 shrink-0" /> {rec.email}</p>}
                {rec.phone && <p className="flex items-center gap-2 text-muted-foreground"><Phone className="h-3.5 w-3.5 shrink-0" /> {rec.phone}</p>}
                {rec.last_interaction_date && <p className="text-muted-foreground text-xs">Last interaction: {fmtDate(rec.last_interaction_date)}</p>}
                <p className={`flex items-center gap-2 text-sm ${followUpTone(rec)}`}>
                  <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                  {rec.next_follow_up_date ? `Follow up: ${fmtDate(rec.next_follow_up_date)}` : "No follow-up scheduled"}
                </p>
              </div>

              {rec.notes && <p className="mt-3 text-sm text-foreground/70 line-clamp-3">{rec.notes}</p>}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="crm-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit record" : "Add record"}</DialogTitle>
            <DialogDescription>Leads, customers and follow-up details for this contact.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" data-testid="crm-form-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Business / organization</Label>
              <Input value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} placeholder="Acme Co." data-testid="crm-form-org" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@example.com" data-testid="crm-form-email" />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" data-testid="crm-form-phone" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Stage</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger data-testid="crm-form-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Last interaction</Label>
                <Input type="date" value={form.last_interaction_date} onChange={(e) => setForm({ ...form, last_interaction_date: e.target.value })} data-testid="crm-form-last" />
              </div>
              <div className="space-y-1.5">
                <Label>Next follow up</Label>
                <Input type="date" value={form.next_follow_up_date} onChange={(e) => setForm({ ...form, next_follow_up_date: e.target.value })} data-testid="crm-form-next" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Context worth remembering…" rows={3} data-testid="crm-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90" data-testid="crm-save-button">
              {saving ? "Saving…" : editingId ? "Save changes" : "Add record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>{deleting?.name} will be permanently removed from your CRM. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="rounded-full bg-destructive hover:bg-destructive/90" data-testid="crm-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
